import { acquireApiToken } from "../auth";

export type ApiContext = {
  tenantUuid: string;
  subtenantUuid: string;
};

export type ApiWarning = {
  code: string;
  message: string;
};

export type PaginatedResult<T> = {
  hasMore: boolean;
  items: T[];
  nextPage?: number;
  page: number;
  pageSize: number;
  warnings?: ApiWarning[];
};

export type SearchPageParams = {
  filterParams?: Record<string, string>;
  page?: number;
  pageSize?: number;
  query: string;
};

export type PreviewStatus = "ready" | "pending" | "warning" | "error";

export type FilterOption = {
  categoryName?: string;
  label: string;
  params?: Record<string, string>;
  subCategory?: string;
  value: string;
};

export type FilterGroup = {
  categoryName?: string;
  key?: string;
  label: string;
  options: FilterOption[];
};

export type AuthSession = {
  authenticated: true;
  active_tenant_uuid: string;
  active_subtenant_uuid: string;
  tenants: Array<{
    tenant_uuid: string;
    tenant_key: string | null;
    tenant_display_name: string | null;
    role: string;
    subtenants: Array<{
      tenant_uuid: string;
      subtenant_uuid: string;
      subtenant_key: string | null;
      subtenant_name: string | null;
      subtenant_display_name: string | null;
      concurrency_level: number | null;
      role: string;
    }>;
  }>;
  user: {
    user_uuid: string;
    external_subject: string;
    external_tenant_id: string | null;
    email: string | null;
    display_name: string | null;
  };
};

export type SlideResult = {
  contentUuid?: string;
  fileUuid: string;
  id: string;
  previewRetryAfterSeconds?: number;
  previewStatus: PreviewStatus;
  slideNumber?: number;
  sourceFileId?: string;
  sourceFileName?: string;
  sourceKind: "template" | "content_file_slide";
  sourceUpdatedAt?: string;
  templateKind?: "content_template" | "design_template";
  templateUuid?: string;
  thumbnailUrl?: string;
  title: string;
};

export type ContentFile = {
  canOpenSlides?: boolean;
  categoryName?: string;
  contentClass: "sourcecontent" | "asset";
  contentUuid: string;
  disabledReason?: string;
  fileSizeBytes?: number;
  fileUuid: string;
  id: string;
  maxOpenFileSizeBytes?: number;
  name: string;
  previewRetryAfterSeconds?: number;
  previewStatus: PreviewStatus;
  slideCount?: number;
  subCategory?: string;
  thumbnailUrl?: string;
  updatedAt?: string;
};

export type InsertPackage = {
  downloadUrl: string;
  expiresAt: string;
  formatting:
    | PowerPoint.InsertSlideFormatting
    | "KeepSourceFormatting"
    | "UseDestinationTheme";
  packageUuid: string;
  rawPptxBytes: number;
};

type BackendErrorBody = {
  code?: string;
  details?: Record<string, unknown>;
  error?: string;
  message?: string;
  request_id?: string;
};

type BackendPaginatedResult<T> = {
  has_more: boolean;
  items: T[];
  next_page?: number;
  page: number;
  page_size: number;
  warnings?: ApiWarning[];
};

type BackendFilterOption = {
  categoryName?: string;
  category_name?: string;
  label: string;
  params?: Record<string, string>;
  subCategory?: string;
  sub_category?: string;
  value: string;
};

type BackendFilterGroup = {
  categoryName?: string;
  category_name?: string;
  key?: string;
  label: string;
  options: BackendFilterOption[];
};

type BackendFilterResponse = {
  groups?: BackendFilterGroup[];
};

type BackendTemplateItem = {
  chapter_semantic?: string;
  description?: string;
  file_uuid: string;
  id: string;
  language_code?: string;
  name: string;
  preview_retry_after_seconds?: number;
  preview_status: PreviewStatus;
  preview_url?: string;
  summary?: string;
  template_kind: "content_template" | "design_template";
  template_uuid: string;
  thumbnail_url?: string;
  updated_at?: string;
};

type BackendContentFileItem = {
  can_open_slides?: boolean;
  category_name?: string;
  content_class: "sourcecontent" | "asset";
  content_title?: string;
  content_uuid: string;
  disabled_reason?: string;
  file_name: string;
  file_size_bytes?: number;
  file_uuid: string;
  id: string;
  insert_disabled_reason?: string;
  max_insert_raw_pptx_bytes?: number;
  max_open_file_size_bytes?: number;
  preview_retry_after_seconds?: number;
  preview_status: PreviewStatus;
  raw_pptx_bytes?: number;
  slide_count?: number | null;
  sub_category?: string;
  thumbnail_url?: string;
  updated_at?: string;
};

type BackendContentSlideItem = {
  content_uuid: string;
  file_uuid: string;
  id: string;
  name?: string;
  preview_retry_after_seconds?: number;
  preview_status: PreviewStatus;
  preview_url?: string;
  slide_number: number;
  thumbnail_url?: string;
};

type BackendInsertPackage = {
  download_url: string;
  expires_at: string;
  formatting:
    | PowerPoint.InsertSlideFormatting
    | "KeepSourceFormatting"
    | "UseDestinationTheme";
  package_uuid: string;
  raw_pptx_bytes: number;
};

type BackendInsertPackageResponse = {
  packages: BackendInsertPackage[];
};

type RequestOptions = {
  body?: unknown;
  context?: ApiContext;
  method?: "GET" | "POST";
};

export class AddInApiError extends Error {
  code?: string;
  details?: Record<string, unknown>;
  requestId?: string;
  status: number;

  constructor(message: string, status: number, body?: BackendErrorBody) {
    super(message);
    this.name = "AddInApiError";
    this.status = status;
    this.code = body?.code ?? body?.error;
    this.details = body?.details;
    this.requestId = body?.request_id;
  }
}

function apiBaseUrl() {
  return String(import.meta.env.ADDIN_VITE_API_BASE_URL ?? "")
    .trim()
    .replace(/\/+$/, "");
}

function resolveApiUrl(pathOrUrl: string) {
  if (/^https?:\/\//i.test(pathOrUrl)) {
    return pathOrUrl;
  }

  const baseUrl = apiBaseUrl();
  if (!baseUrl) {
    throw new Error("ADDIN_VITE_API_BASE_URL is not configured.");
  }

  return `${baseUrl}${pathOrUrl.startsWith("/") ? pathOrUrl : `/${pathOrUrl}`}`;
}

function normalizeContextValue(value: unknown) {
  return typeof value === "string" ? value.trim() : "";
}

function assertApiContext(context: ApiContext): ApiContext {
  const tenantUuid = normalizeContextValue(context.tenantUuid);
  const subtenantUuid = normalizeContextValue(context.subtenantUuid);

  if (!tenantUuid || !subtenantUuid) {
    throw new AddInApiError("Could not load account context.", 0, {
      error: "account_context_unavailable",
      message: "Could not load account context.",
    });
  }

  return { tenantUuid, subtenantUuid };
}

async function parseErrorBody(
  response: Response,
): Promise<BackendErrorBody | undefined> {
  try {
    return (await response.json()) as BackendErrorBody;
  } catch {
    return undefined;
  }
}

async function request<T>(
  pathOrUrl: string,
  options: RequestOptions = {},
): Promise<T> {
  const token = await acquireApiToken();
  if (!token) {
    throw new AddInApiError("Please sign in again.", 0, {
      error: "api_token_unavailable",
      message: "Please sign in again.",
    });
  }

  const headers: Record<string, string> = {
    Accept: "application/json",
    Authorization: `Bearer ${token}`,
  };

  if (options.context) {
    const context = assertApiContext(options.context);
    headers["X-Tenant-UUID"] = context.tenantUuid;
    headers["X-Subtenant-UUID"] = context.subtenantUuid;
  }

  if (options.body !== undefined) {
    headers["Content-Type"] = "application/json";
  }

  const response = await fetch(resolveApiUrl(pathOrUrl), {
    body: options.body === undefined ? undefined : JSON.stringify(options.body),
    headers,
    method: options.method ?? (options.body === undefined ? "GET" : "POST"),
  });

  if (!response.ok) {
    const errorBody = await parseErrorBody(response);
    throw new AddInApiError(
      errorBody?.message ?? `API request failed with ${response.status}.`,
      response.status,
      errorBody,
    );
  }

  try {
    return (await response.json()) as T;
  } catch {
    throw new AddInApiError("Invalid API response.", response.status, {
      error: "invalid_response",
      message: "Invalid API response.",
    });
  }
}

async function requestArrayBuffer(
  pathOrUrl: string,
  context: ApiContext,
): Promise<ArrayBuffer> {
  const token = await acquireApiToken();
  if (!token) {
    throw new AddInApiError("Please sign in again.", 0, {
      error: "api_token_unavailable",
      message: "Please sign in again.",
    });
  }

  const scopedContext = assertApiContext(context);
  const response = await fetch(resolveApiUrl(pathOrUrl), {
    headers: {
      Accept:
        "application/vnd.openxmlformats-officedocument.presentationml.presentation,application/octet-stream",
      Authorization: `Bearer ${token}`,
      "X-Tenant-UUID": scopedContext.tenantUuid,
      "X-Subtenant-UUID": scopedContext.subtenantUuid,
    },
  });

  if (!response.ok) {
    const errorBody = await parseErrorBody(response);
    throw new AddInApiError(
      errorBody?.message ?? `Package download failed with ${response.status}.`,
      response.status,
      errorBody,
    );
  }

  return response.arrayBuffer();
}

function appendQuery(params: SearchPageParams) {
  const searchParams = new URLSearchParams();
  const query = params.query.trim();

  if (query) {
    searchParams.set("query", query);
  }

  searchParams.set("page", String(params.page ?? 1));
  searchParams.set("page_size", String(params.pageSize ?? 10));

  Object.entries(params.filterParams ?? {}).forEach(([key, value]) => {
    if (value) {
      searchParams.set(key, value);
    }
  });

  return searchParams.toString();
}

function mapPaginatedResult<BackendItem, Item>(
  result: BackendPaginatedResult<BackendItem>,
  mapper: (item: BackendItem) => Item,
): PaginatedResult<Item> {
  return {
    hasMore: result.has_more,
    items: result.items.map(mapper),
    nextPage: result.next_page,
    page: result.page,
    pageSize: result.page_size,
    warnings: result.warnings,
  };
}

function mapFilterOption(option: BackendFilterOption): FilterOption {
  return {
    categoryName: option.categoryName ?? option.category_name,
    label: option.label,
    params: option.params,
    subCategory: option.subCategory ?? option.sub_category,
    value: option.value,
  };
}

function mapFilterGroups(response: BackendFilterResponse): FilterGroup[] {
  return (response.groups ?? []).map((group) => ({
    categoryName: group.categoryName ?? group.category_name,
    key: group.key,
    label: group.label,
    options: group.options.map(mapFilterOption),
  }));
}

function mapTemplateItem(item: BackendTemplateItem): SlideResult {
  return {
    fileUuid: item.file_uuid,
    id: item.id || item.template_uuid,
    previewRetryAfterSeconds: item.preview_retry_after_seconds,
    previewStatus: item.preview_status,
    sourceKind: "template",
    sourceUpdatedAt: item.updated_at,
    templateKind: item.template_kind,
    templateUuid: item.template_uuid,
    thumbnailUrl: item.preview_url ?? item.thumbnail_url,
    title: item.name,
  };
}

function mapContentFileItem(item: BackendContentFileItem): ContentFile {
  return {
    canOpenSlides: item.can_open_slides,
    categoryName: item.category_name,
    contentClass: item.content_class,
    contentUuid: item.content_uuid,
    disabledReason: item.disabled_reason ?? item.insert_disabled_reason,
    fileSizeBytes: item.file_size_bytes ?? item.raw_pptx_bytes,
    fileUuid: item.file_uuid,
    id: item.id || item.content_uuid,
    maxOpenFileSizeBytes:
      item.max_open_file_size_bytes ?? item.max_insert_raw_pptx_bytes,
    name: item.file_name || item.content_title || "Untitled presentation",
    previewRetryAfterSeconds: item.preview_retry_after_seconds,
    previewStatus: item.preview_status,
    slideCount: item.slide_count ?? undefined,
    subCategory: item.sub_category,
    thumbnailUrl: item.thumbnail_url,
    updatedAt: item.updated_at,
  };
}

function mapContentSlideItem(
  item: BackendContentSlideItem,
  file: ContentFile,
): SlideResult {
  return {
    contentUuid: item.content_uuid,
    fileUuid: item.file_uuid,
    id:
      item.id || `${item.content_uuid}:${item.file_uuid}:${item.slide_number}`,
    previewRetryAfterSeconds: item.preview_retry_after_seconds,
    previewStatus: item.preview_status,
    slideNumber: item.slide_number,
    sourceFileId: file.id,
    sourceFileName: file.name,
    sourceKind: "content_file_slide",
    thumbnailUrl: item.preview_url ?? item.thumbnail_url,
    title: item.name || `Slide ${item.slide_number}`,
  };
}

function mapInsertPackage(item: BackendInsertPackage): InsertPackage {
  return {
    downloadUrl: item.download_url,
    expiresAt: item.expires_at,
    formatting: item.formatting,
    packageUuid: item.package_uuid,
    rawPptxBytes: item.raw_pptx_bytes,
  };
}

export function contextFromSession(session: AuthSession): ApiContext {
  return assertApiContext({
    subtenantUuid: session.active_subtenant_uuid,
    tenantUuid: session.active_tenant_uuid,
  });
}

export async function fetchAuthSession(): Promise<AuthSession> {
  return request<AuthSession>("/api/auth/session");
}

export async function fetchTemplateFilters(
  context: ApiContext,
): Promise<FilterGroup[]> {
  return mapFilterGroups(
    await request<BackendFilterResponse>("/api/add-in/templates/filters", {
      context,
    }),
  );
}

export async function fetchContentFilters(
  context: ApiContext,
): Promise<FilterGroup[]> {
  return mapFilterGroups(
    await request<BackendFilterResponse>("/api/add-in/content/files/filters", {
      context,
    }),
  );
}

export async function searchTemplateSlides(
  context: ApiContext,
  params: SearchPageParams,
): Promise<PaginatedResult<SlideResult>> {
  const query = appendQuery(params);
  const result = await request<BackendPaginatedResult<BackendTemplateItem>>(
    `/api/add-in/templates?${query}`,
    { context },
  );
  return mapPaginatedResult(result, mapTemplateItem);
}

export async function searchContentFiles(
  context: ApiContext,
  params: SearchPageParams,
): Promise<PaginatedResult<ContentFile>> {
  const query = appendQuery(params);
  const result = await request<BackendPaginatedResult<BackendContentFileItem>>(
    `/api/add-in/content/files?${query}`,
    { context },
  );
  return mapPaginatedResult(result, mapContentFileItem);
}

export async function fetchContentFileSlides(
  context: ApiContext,
  file: ContentFile,
  params: Pick<SearchPageParams, "page" | "pageSize">,
): Promise<PaginatedResult<SlideResult>> {
  const searchParams = new URLSearchParams();
  searchParams.set("file_uuid", file.fileUuid);
  searchParams.set("page", String(params.page ?? 1));
  searchParams.set("page_size", String(params.pageSize ?? 10));

  const result = await request<BackendPaginatedResult<BackendContentSlideItem>>(
    `/api/add-in/content/files/${encodeURIComponent(file.contentUuid)}/slides?${searchParams.toString()}`,
    { context },
  );

  return mapPaginatedResult(result, (item) => mapContentSlideItem(item, file));
}

export async function createInsertPackages(
  context: ApiContext,
  slides: SlideResult[],
  keepFormatting: boolean,
): Promise<InsertPackage[]> {
  const response = await request<BackendInsertPackageResponse>(
    "/api/add-in/insert-packages",
    {
      body: {
        items: slides.map((slide) => {
          if (slide.sourceKind === "template") {
            if (!slide.templateKind || !slide.templateUuid) {
              throw new Error(
                "Selected template is missing insertion metadata.",
              );
            }

            return {
              file_uuid: slide.fileUuid,
              source_kind: "template",
              template_kind: slide.templateKind,
              template_uuid: slide.templateUuid,
            };
          }

          if (!slide.contentUuid || !slide.slideNumber) {
            throw new Error("Selected slide is missing insertion metadata.");
          }

          return {
            content_uuid: slide.contentUuid,
            file_uuid: slide.fileUuid,
            slide_number: slide.slideNumber,
            source_kind: "content_file_slide",
          };
        }),
        keep_formatting: keepFormatting,
      },
      context,
      method: "POST",
    },
  );

  return response.packages.map(mapInsertPackage);
}

export async function downloadInsertPackage(
  context: ApiContext,
  packageUrl: string,
): Promise<ArrayBuffer> {
  return requestArrayBuffer(packageUrl, context);
}

function getNumericDetail(value: unknown) {
  return typeof value === "number" && Number.isFinite(value) ? value : null;
}

function formatMegabytes(bytes: number) {
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

export function getApiErrorMessage(error: unknown, fallback: string) {
  if (error instanceof AddInApiError) {
    if (error.code === "invalid_response") {
      return fallback;
    }

    if (error.code === "api_token_unavailable") {
      return "Please sign in again.";
    }

    if (error.code === "onboarding_required") {
      return "Your account is not registered for this add-in.";
    }

    if (error.code === "source_file_changed") {
      return "The selected file changed. Refresh and try again.";
    }

    if (error.code === "source_file_not_found") {
      return "The selected file is no longer available.";
    }

    if (error.code === "insert_package_too_large") {
      const rawPptxBytes = getNumericDetail(error.details?.raw_pptx_bytes);
      const maxRawPptxBytes = getNumericDetail(
        error.details?.max_raw_pptx_bytes,
      );

      if (rawPptxBytes && maxRawPptxBytes) {
        return `The selected slides are too large to insert (${formatMegabytes(rawPptxBytes)}; limit ${formatMegabytes(maxRawPptxBytes)}).`;
      }

      return "The selected slides are too large to insert.";
    }

    return error.message || fallback;
  }

  if (error instanceof TypeError) {
    return fallback;
  }

  if (error instanceof Error) {
    if (
      error.message === "No API access token is available." ||
      error.message.includes("ADDIN_VITE_API_BASE_URL")
    ) {
      return fallback;
    }

    return error.message || fallback;
  }

  return fallback;
}
